import type { JobEditProductItem, JobEditServiceItem } from '@/models/jobEdit';

import { computed, reactive, ref, watch } from 'vue';
import { type Operation, compare } from 'fast-json-patch';
import { cloneDeep } from 'lodash';

import { useJobEditSectionBase } from '@/composables/jobEdit/useJobEditSectionBase';
import { useJobEditStore } from '@/stores/useJobEditStore';
import {
  adaptProductToApi,
  adaptServiceToApi,
} from '@/helpers/adapters/jobEditProductsServicesAdapters';
import * as fetchers from '@/helpers/fetchers';
import { KJobEditError } from '@/helpers/throwables';

interface ProductsServicesData {
  products: JobEditProductItem[];
  services: JobEditServiceItem[];
  instructions: string;
}

export interface ServicesTotals {
  quantity: number;
  scheduledFee: number;
  fee: number;
  gst: number;
  secondQuantity: number;
  pay: number;
}

export function useJobEditProductsServices(
  stopNumber: number,
  initialProducts: () => JobEditProductItem[],
  initialServices: () => JobEditServiceItem[],
  initialInstructions: () => string,
  onStopTotalChange?: (total: ServicesTotals) => void,
) {
  const jobEditStore = useJobEditStore();

  const formData = reactive<ProductsServicesData>({
    products: cloneDeep(initialProducts()),
    services: cloneDeep(initialServices()),
    instructions: initialInstructions(),
  });

  const originalData = ref<ProductsServicesData>({
    products: cloneDeep(initialProducts()),
    services: cloneDeep(initialServices()),
    instructions: initialInstructions(),
  });

  function syncWithStoreData(): void {
    const products = cloneDeep(initialProducts());
    const services = cloneDeep(initialServices());
    const instructions = initialInstructions();
    originalData.value = { products, services, instructions };
    Object.assign(formData, cloneDeep({ products, services, instructions }));
  }

  const stopTotal = computed<ServicesTotals>(() =>
    formData.services.reduce(
      (acc, s) => ({
        quantity: acc.quantity + (s.quantity || 0),
        scheduledFee: acc.scheduledFee + (s.scheduled_fee || 0),
        fee: acc.fee + (s.fee || 0),
        gst: acc.gst + (s.gst || 0),
        secondQuantity: acc.secondQuantity + (s.second_quantity || 0),
        pay: acc.pay + (s.pay || 0),
      }),
      {
        quantity: 0,
        scheduledFee: 0,
        fee: 0,
        gst: 0,
        secondQuantity: 0,
        pay: 0,
      },
    ),
  );

  watch(stopTotal, (total) => onStopTotalChange?.(total), { immediate: true });

  function buildPatchDocument(): Operation[] {
    const stopIndex = stopNumber - 1;
    const padding = Array<null>(stopIndex).fill(null);

    const originalStop = {
      products: originalData.value.products.map(adaptProductToApi),
      services: originalData.value.services.map(adaptServiceToApi),
      instructions: originalData.value.instructions,
    };
    const currentStop = {
      products: formData.products.map(adaptProductToApi),
      services: formData.services.map(adaptServiceToApi),
      instructions: formData.instructions,
    };

    return compare(
      cloneDeep({ stops: [...padding, originalStop] }),
      cloneDeep({ stops: [...padding, currentStop] }),
    );
  }

  async function saveProductsServices(): Promise<boolean> {
    const jobId = jobEditStore.jobId;
    const jobDate = jobEditStore.jobDate;

    if (!jobId || !jobDate) {
      throw new KJobEditError('Job ID or Job Date is not available');
    }

    const patchOps = buildPatchDocument();

    if (patchOps.length === 0) {
      return true;
    }

    await fetchers.fetchPutJobeditStopContentSection(
      {
        job_id: jobId,
        job_date: jobDate,
        stop_number: String(stopNumber),
      },
      patchOps,
    );

    return true;
  }

  function resetForm(): void {
    Object.assign(formData, cloneDeep(originalData.value));
  }

  const {
    startEditing: baseStartEditing,
    cancelEditing,
    saveEditing,
    isEditing,
    isLoading,
  } = useJobEditSectionBase({
    sectionName: `products-services-stop-${stopNumber}`,
    saveOperation: saveProductsServices,
    onCancel: resetForm,
  });

  async function startEditing(): Promise<void> {
    syncWithStoreData();
    await baseStartEditing();
  }

  return {
    startEditing,
    cancelEditing,
    saveEditing,
    isEditing,
    isLoading,
    formData,
    stopTotal,
  };
}
